import { useCallback, useEffect, useMemo, useState } from "react";
import API from "../../../services/api";
import { fetchMyAttendance } from "../attendanceApi";
import { fetchWfHolidays } from "../wfApi";
import { mapAttendanceRow } from "../attendanceService";
import {
  buildAttendanceDayMap,
  buildCalendarCells,
  buildHolidayDateMap,
  buildHolidayDayMap,
  mergeAttendanceDisplayMap,
  monthRange,
  parseWorkDate,
  summarizeStatusMap,
  fixedWorkingDaysFromPayroll,
  weekOffPolicyFromAttendance,
} from "../attendanceCalendarUtils";

export function useMyAttendance({ employeeId, period, calendarMonthKey }) {
  const [rawRows, setRawRows] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [attendanceSettings, setAttendanceSettings] = useState(null);
  const [fixedWorkingDays, setFixedWorkingDays] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!employeeId || !period?.from_date || !period?.to_date) {
      setRawRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [attendance, holidayRows, settingsRes] = await Promise.all([
        fetchMyAttendance({ from_date: period.from_date, to_date: period.to_date }),
        fetchWfHolidays({ from_date: period.from_date, to_date: period.to_date }).catch(() => []),
        API.get("/organisation/me/hr-settings").catch(() => null),
      ]);
      setRawRows(Array.isArray(attendance) ? attendance : []);
      setHolidays(Array.isArray(holidayRows) ? holidayRows : []);
      const att =
        settingsRes?.data?.attendance_settings ||
        settingsRes?.data?.hr_settings?.attendance ||
        null;
      setAttendanceSettings(att);
      setFixedWorkingDays(fixedWorkingDaysFromPayroll(settingsRes?.data?.payroll_settings || {}));
    } catch (e) {
      setRawRows([]);
      setHolidays([]);
      setAttendanceSettings(null);
      setFixedWorkingDays(null);
      setError(e?.message || "Failed to load your attendance");
    } finally {
      setLoading(false);
    }
  }, [employeeId, period]);

  useEffect(() => {
    load();
  }, [load]);

  const mappedRows = useMemo(
    () => rawRows.map(mapAttendanceRow).filter((r) => r.date),
    [rawRows]
  );

  const byDate = useMemo(() => {
    const map = new Map();
    mappedRows.forEach((r) => map.set(r.date, r));
    return map;
  }, [mappedRows]);

  const calendar = useMemo(() => buildCalendarCells(calendarMonthKey), [calendarMonthKey]);

  const rowsByDay = useMemo(() => {
    const map = {};
    for (const r of rawRows || []) {
      const parts = parseWorkDate(r.work_date);
      if (!parts || parts.monthKey !== calendarMonthKey) continue;
      map[parts.day] = r;
    }
    return map;
  }, [rawRows, calendarMonthKey]);

  const weekOffPolicy = useMemo(
    () => (attendanceSettings ? weekOffPolicyFromAttendance(attendanceSettings) : null),
    [attendanceSettings]
  );

  const dayStatusMap = useMemo(() => {
    const base = buildAttendanceDayMap(rawRows, calendarMonthKey);
    return mergeAttendanceDisplayMap(
      base,
      calendar.cells,
      buildHolidayDayMap(holidays, calendarMonthKey),
      {
        ...(weekOffPolicy || {}),
        monthKey: calendarMonthKey,
        fixedWorkingDays,
        rowsByDay,
      }
    );
  }, [rawRows, calendarMonthKey, calendar.cells, holidays, weekOffPolicy, fixedWorkingDays, rowsByDay]);

  const holidayByDate = useMemo(() => buildHolidayDateMap(holidays), [holidays]);
  const holidayDayMap = useMemo(
    () => buildHolidayDayMap(holidays, calendarMonthKey),
    [holidays, calendarMonthKey]
  );

  const monthSummary = useMemo(() => {
    const daysInMonth = calendar.range?.daysInMonth;
    if (!daysInMonth) return null;
    return summarizeStatusMap(dayStatusMap, daysInMonth);
  }, [dayStatusMap, calendar.range]);

  const totalHours = useMemo(
    () => mappedRows.reduce((sum, r) => sum + (Number(r.hoursWorked) || 0), 0),
    [mappedRows]
  );

  const sortedRows = useMemo(
    () => [...mappedRows].sort((a, b) => a.date.localeCompare(b.date)),
    [mappedRows]
  );

  const attendanceRate = useMemo(() => {
    if (!monthSummary?.marked || !calendar.range?.daysInMonth) return 0;
    const productive = monthSummary.P + monthSummary.HD * 0.5;
    const denom = Math.max(monthSummary.marked, 1);
    return Math.min(100, Math.round((productive / denom) * 100));
  }, [monthSummary, calendar.range]);

  return {
    rawRows,
    mappedRows,
    sortedRows,
    byDate,
    holidays,
    holidayByDate,
    holidayDayMap,
    dayStatusMap,
    monthSummary,
    totalHours,
    attendanceRate,
    calendar,
    loading,
    error,
    reload: load,
  };
}

export function resolvePeriod(viewMode, monthKey, customFrom, customTo, formatMonthLabel) {
  if (viewMode === "custom") {
    if (!customFrom || !customTo) return null;
    return { from_date: customFrom, to_date: customTo, label: `${customFrom} → ${customTo}` };
  }
  const range = monthRange(monthKey);
  if (!range) return null;
  return {
    from_date: range.from_date,
    to_date: range.to_date,
    label: formatMonthLabel(monthKey),
  };
}
